import { computed, signal, Signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DatapointAdapter, DatapointView, CompareEntry, pctDelta } from '../datapoint.model';

/** One SAFECOR reading, tagged with the electrode that produced it. */
export interface SafecorRow {
  electrode: 1 | 2;
  time: Date | string;
  [variable: string]: unknown;
}

export interface SafecorAdapterDeps {
  measurementName: string;
  /** Rows currently plotted (any electrode mix), time-sorted. */
  rows: SafecorRow[];
  /** Index in `rows` of the clicked point. */
  startIndex: number;
  series: { key: string; label: string; unit?: string }[];
  /** "Electrode 1" etc — injected so the adapter stays translation-free. */
  electrodeLabel: (electrode: 1 | 2) => string;
  highlightKey?: string;
}

/**
 * Navigates the readings of ONE electrode (the clicked point's), even when
 * the chart plots both interleaved — prev/next and compare deltas between
 * readings of different electrodes would be meaningless.
 */
export class SafecorDatapointAdapter implements DatapointAdapter {
  private readonly seq: SafecorRow[];
  private readonly cursor: ReturnType<typeof signal<number>>;
  readonly current: Signal<DatapointView>;
  readonly canDelete = computed(() => false);

  constructor(private readonly deps: SafecorAdapterDeps) {
    const clicked = deps.rows[deps.startIndex];
    this.seq = deps.rows.filter((r) => r.electrode === clicked.electrode);
    this.cursor = signal(Math.max(0, this.seq.indexOf(clicked)));
    this.current = computed(() => this.buildView(this.cursor()));
  }

  next(): void {
    this.cursor.update((i) => Math.min(i + 1, this.seq.length - 1));
  }
  prev(): void {
    this.cursor.update((i) => Math.max(i - 1, 0));
  }
  delete(): Observable<unknown> {
    return of(null); // not supported for SAFECOR
  }

  private compareAgainst(a: SafecorRow, b: SafecorRow): CompareEntry[] {
    return this.deps.series
      .map((s) => {
        const x = a[s.key];
        const y = b[s.key];
        if (typeof x !== 'number' || typeof y !== 'number') return null;
        return { key: s.key, label: s.label, delta: x - y, pct: pctDelta(y, x) };
      })
      .filter((c): c is CompareEntry => c !== null);
  }

  private buildView(i: number): DatapointView {
    const row = this.seq[i];
    const total = this.seq.length;
    const view: DatapointView = {
      sourceLabel: `${this.deps.measurementName} · ${this.deps.electrodeLabel(row.electrode)}`,
      timestamp: new Date(row.time),
      values: this.deps.series.map((s) => ({
        key: s.key, label: s.label, unit: s.unit,
        value: (row[s.key] as number | string | null | undefined) ?? null,
      })),
      highlightKey: this.deps.highlightKey,
      nav: { index: i, total, hasPrev: i > 0, hasNext: i < total - 1 },
      compare: {},
      attachments: [],
    };
    if (i > 0) view.compare.prev = this.compareAgainst(row, this.seq[i - 1]);
    if (i < total - 1) view.compare.next = this.compareAgainst(row, this.seq[i + 1]);
    return view;
  }
}
